import {Injectable, OnDestroy} from "@angular/core";
import {BaseStore} from "../../../../store/core/base-store";
import {EventAnalysisStoreService} from "../../../../store/event-analysis-store.service";
import {LocationDto} from "../../../../../../../models/location-dto";
import {LinkedDtoRepository} from "../../../../../common/repositories/linked-dto-repository";
import {TopGridProcessor} from "../utils/top-grid-processor";
import {TopSalesBaseElement} from "../models/top-sales-base-element";
import {SalesRepository} from "../../../../repositories/core/sales-repository/sales-repository";
import {CompositionVariantDto} from "../../../../models/sales/composition-variant-dto";
import {
  ModificationLineTransfer
} from "../../../../../../../_models/_booking/composition-override-linker-transfer";


@Injectable()
export class TopSalesStore extends BaseStore implements OnDestroy {
  topSalesData: { [id: string]: TopSalesBaseElement[] } = {};
  locationTranslationMapping: LocationDto[] | undefined = [];
  activeLocations: string[] = [];
  topSalesSortedView: TopSalesBaseElement[] = [];
  sortedByTotalSalesRevenue: boolean = true;

  constructor(
    public eventAnalysisStoreService: EventAnalysisStoreService,
    public linkedDtoRepository: LinkedDtoRepository<any>,
    public salesRepository: SalesRepository
  ) {
    super(eventAnalysisStoreService);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
  }

  override update() {
    this.processTopSalesRanking()
  }

  override initialize() {
    this.processTopSalesRanking()
    this.locationTranslationMapping = Object.values(this.linkedDtoRepository.getMapping("locationId")) as LocationDto[];
    this.activeLocations = this.locationTranslationMapping.map(x => x.id)
    this.mapActiveLocationsToTopSalesSortedView()
  }

  processTopSalesRanking() {
    // #534: the ranking follows the SHARED "Kombinationen einzeln" toggle — no ranking-local switch. A mode
    // flip reaches here through the store service's normal update cycle, so nothing else needs re-registering.
    this.topSalesData = TopGridProcessor.processLocationDataForTopSalesRanking(this.salesRepository.getSalesWrapper(), this.linkedDtoRepository, this.eventAnalysisStoreService.getRevenueCalculation(), this.eventAnalysisStoreService.compositionRowKeyFn());
  }


  /**
   * Resolves a ranking entry's name. The lookup goes through the mapping matching the ACTIVE grouping mode:
   * `compositionId` → the plain article name, `compositionVariantKey` → the variant label (article name +
   * its `├`/`└` modification lines, newline-joined). Asking the wrong mapping simply misses and renders an
   * empty name, which is why the key comes from the one store-level helper.
   */
  mapCompositionIdToName(composition: TopSalesBaseElement): string {
    return this.linkedDtoRepository.getName(composition.id, this.eventAnalysisStoreService.compositionRowKey()) ?? "";
  }

  /**
   * The ranking entry's **headline**: the article name on its own, without its modification lines.
   *
   * In combined mode the active mapping is the plain `compositionId` one, whose `name` already is the article
   * name. In individual mode it is the variant mapping, whose `name` is the newline-joined label — so the
   * article name is read off {@link CompositionVariantDto.articleName} instead of being sliced out of that
   * string.
   */
  mapCompositionIdToArticleName(composition: TopSalesBaseElement): string {
    return this.variantModel(composition)?.articleName ?? this.mapCompositionIdToName(composition);
  }

  /**
   * The entry's swap combination as `{connector, text}` PAIRS, so the template can render the box-drawing
   * connector in its own fixed `.modification-connector` column — exactly like the composition table and the
   * event-details article list.
   *
   * This replaced a `mapCompositionIdToLabelLines()` that split the joined label on `\n` and printed each line
   * as text. That put `├` and `└` inside the proportional body font, where they have different glyph widths,
   * so the last sub-line sat visibly out of column against the ones above it. Returning the parts instead of a
   * pre-joined string is what makes the shared connector column usable here.
   *
   * Empty in combined mode and for an unmodified article, which is when the template renders no sub-lines.
   */
  mapCompositionIdToModificationLines(composition: TopSalesBaseElement): ModificationLineTransfer[] {
    return this.variantModel(composition)?.modificationLines ?? [];
  }

  /**
   * The active mapping's entry for this ranking id, when it is a variant entry. Only the
   * `"compositionVariantKey"` mapping holds {@link CompositionVariantDto}s, so this is `undefined` in combined
   * mode — and also for an id the mapping does not know, which is why every caller has a fallback.
   */
  private variantModel(composition: TopSalesBaseElement): CompositionVariantDto | undefined {
    let model = this.linkedDtoRepository.getMapping(this.eventAnalysisStoreService.compositionRowKey())[composition.id];
    return model instanceof CompositionVariantDto ? model : undefined;
  }

  /** `true` while the ranking is split per swap combination — the card header shows a hint badge then. */
  isIndividualGrouping(): boolean {
    return this.eventAnalysisStoreService.groupingMode() === 'individual';
  }

  mapActiveLocationsToTopSalesSortedView(): TopSalesBaseElement[] {

    this.topSalesSortedView = [];
    let cacheMap: { [id: string]: TopSalesBaseElement } = {};
    for (const locationId of this.activeLocations) {
      if (!this.topSalesData[locationId]) {
        continue;
      }
      for (const topSalesElement of this.topSalesData[locationId]) {
        if (cacheMap[topSalesElement.id]) {
          cacheMap[topSalesElement.id].totalSalesRevenue += topSalesElement.totalSalesRevenue;
          cacheMap[topSalesElement.id].topSalesQuantity += topSalesElement.topSalesQuantity;
        } else {
          cacheMap[topSalesElement.id] = new TopSalesBaseElement(topSalesElement.id, topSalesElement.totalSalesRevenue, topSalesElement.topSalesQuantity);
        }
      }
    }
    if (this.sortedByTotalSalesRevenue) {
      this.topSalesSortedView = Object.values(cacheMap).sort((a, b) => b.totalSalesRevenue - a.totalSalesRevenue);
    } else {
      this.topSalesSortedView = Object.values(cacheMap).sort((a, b) => b.topSalesQuantity - a.topSalesQuantity);
    }
    return this.topSalesSortedView;
  }

  isActive(locationId: string): boolean {
    return this.activeLocations.includes(locationId);
  }

  toggleLocationIsActive(locationId: string) {
    if (this.activeLocations.includes(locationId)) {
      this.activeLocations = this.activeLocations.filter(x => x !== locationId);
    } else {
      this.activeLocations.push(locationId);
    }
  }

  mapLocationIdToName(locationId: string): string {
    let location;
    if (this.locationTranslationMapping) {
      location = this.locationTranslationMapping.find(x => x.id === locationId)?.name
    }
    return location ?? "";
  }

  setSortedByTotalSalesRevenue(value: boolean) {
    this.sortedByTotalSalesRevenue = value;
  }

  getSortedByTotalSalesRevenue(): boolean {
    return this.sortedByTotalSalesRevenue;
  }
}
